import {
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  Input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, tap } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  injectCreateQuickStoryReportMutation,
  injectUpdateQuickStoryReportMutation,
} from 'src/app/api/queries/post-api.queries';
import { selectFeed } from 'src/app/core/reducers';

import { AppState } from 'src/app/reducers';
import { FormsService } from 'src/app/core/services/forms.service';
import { NomLanguages } from 'src/app/core/constants';
import { Post } from 'src/app/core/models/core.models';
import { PostDetailsService } from 'src/app/core/services/post-details.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-post-quick-story',
  templateUrl: './post-quick-story.component.html',
  styleUrls: ['./post-quick-story.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostQuickStoryComponent implements OnChanges {
  @Input() postId: string;

  private readonly postDetails = inject(PostDetailsService);

  /** The post this tab is showing - the pane pushes it through `ngOnChanges`. */
  private readonly shownPostId = signal<string | null>(null);

  feedId: string;

  // `toObservable` needs an injection context, so this is a field; `ngOnInit` only
  // subscribes.
  post$: Observable<Post> = toObservable(
    computed(() => this.postDetails.postFor(this.shownPostId())),
  );
  post: Post;
  initialReportContent: string;

  formGroup: FormGroup = new FormGroup({
    language: new FormControl('english', [Validators.required]),
    reportContent: new FormControl(),
  });

  protected utilService = inject(UtilsService);
  protected destroyRef = inject(DestroyRef);

  private readonly createReportMutation =
    injectCreateQuickStoryReportMutation();
  private readonly updateReportMutation =
    injectUpdateQuickStoryReportMutation();

  loadingQuickStoryReport$: Observable<boolean> = toObservable(
    computed(
      () =>
        this.pendingForShownPost(
          this.createReportMutation.isPending(),
          this.createReportMutation.variables(),
        ) ||
        this.pendingForShownPost(
          this.updateReportMutation.isPending(),
          this.updateReportMutation.variables(),
        ),
    ),
  );

  constructor(
    private store: Store<AppState>,
    private formService: FormsService,
  ) {
    //console.log('post full content');
    // @TODO languages to be list from formService
  }

  ngOnInit(): void {
    this.shownPostId.set(this.postId);
    this.store
      .pipe(
        select(selectFeed),
        tap((feed) => {
          this.feedId = feed.id;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.post$
      .pipe(
        tap((post) => {
          this.post = post;
          this.setReportContent();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * The pane keeps one instance of this tab and re-pushes `postId` when the selected
   * post changes, so the panel has to follow it rather than bind once.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postId']?.currentValue) {
      this.shownPostId.set(this.postId);
    }
  }

  private pendingForShownPost(
    isPending: boolean,
    variables: { postId: string } | undefined,
  ): boolean {
    return isPending && variables?.postId === this.shownPostId();
  }

  languageName: string = this.formGroup.get('language').value;

  getLanguageId(languageName: string) {
    switch (languageName) {
      case 'english':
        return NomLanguages.ENGLISH;
      case 'arabic':
        return NomLanguages.ARABIC;
      case 'bulgarian':
        return NomLanguages.BULGARIAN;
      default:
        return null;
    }
  }

  get currentLanguageId() {
    return this.getLanguageId(this.formGroup.get('language').value);
  }

  setReportContent() {
    // `quick_story_report` is absent until a report exists. Indexing undefined
    // throws out of the `post$` subscription and kills it, so guard it here too.
    this.initialReportContent =
      this.post?.quick_story_report?.[this.currentLanguageId]?.html;
    this.formGroup.patchValue({ reportContent: this.initialReportContent });
  }

  generateQuickStoryReport() {
    const languageName = this.formGroup.get('language').value;
    const languageId = this.getLanguageId(languageName);

    this.createReportMutation.mutate({
      postId: this.post.id,
      feedId: this.feedId,
      languageId: languageId,
    });
  }

  copyText() {
    const text = this.formGroup.get('reportContent')?.value;

    this.utilService.copyToClipboard(text);
  }

  updateReport() {
    const languageName = this.formGroup.get('language').value;
    const languageId = this.getLanguageId(languageName);
    const reportContent = this.formGroup.get('reportContent').value;

    this.updateReportMutation.mutate({
      postId: this.post.id,
      languageId: languageId,
      feedId: this.feedId,
      html: reportContent,
    });
  }
}
